import React, { useState } from 'react';
import { GetServerSideProps } from 'next';
import dynamic from 'next/dynamic';
import ApiService from 'server/nextUtils/apiService';
import type { Request } from 'express';
import type { WCardDesignFamilySearchView } from '@zola/svc-web-api-ts-client';
import { UserContext } from '@zola-helpers/client/dist/types/@types';
import { fetchUser } from 'api/server/users';
import { initializeStore } from 'src/store/nextStore';
import { receiveUser } from 'actions/UserActions';
import { getSEOData } from 'cards/seo';
import type { SEOFilters } from 'cards/seo/types';
import { PLP_MODULE_GROUP_TAGS } from 'cards/constants/plpModuleGroupTags';
import { requestModuleGroup, receiveModuleGroup } from 'actions/ModuleGroupActions';
import {
  getMotifsFromUrl,
  getSeoColorsFromUrl,
  getSilhouettesFromUrl,
} from 'cards/components/CardListing/CardListingRedesign/utils';
import { ResponsiveProvider } from 'components/common/ResponsiveContext';
import { getMotifs } from 'cards/actions/cardCatalogActions';
import { handleGetQueryParamsFromUrl } from '../utils/plpUtils';

import { Device } from '../../src/lib/universal/utils/devices';
import { FullSuitePLP } from '../../src/cards/components/FullSuitePLP';

const Footer = dynamic(
  () => import('@zola/zola-ui/src/components/Footer').then(module => module.Footer),
  { ssr: false }
);

const CardsModalRoot = dynamic(() => import('cards/components/common/CardsModalRoot'), {
  ssr: false,
});

interface SuitesPageProps {
  isInitialMobile: boolean;
  isInitialTablet: boolean;
  isInitialDesktop: boolean;
  initialSuites: WCardDesignFamilySearchView | null;
  initialFilters: any;
  userContext: UserContext | null;
  ssrSeoFilters: SEOFilters;
}

export const Page: React.FC<SuitesPageProps> = ({
  isInitialMobile,
  isInitialTablet,
  isInitialDesktop,
  initialSuites,
  initialFilters,
  ssrSeoFilters,
}) => {
  const [ssrSeoData] = useState(getSEOData('MATCHING_SUITES', ssrSeoFilters));
  return (
    <ResponsiveProvider
      isInitialMobile={isInitialMobile}
      isInitialDesktop={isInitialDesktop}
      isInitialTablet={isInitialTablet}
    >
      <div className="container-fluid">
        <FullSuitePLP
          initialSuites={initialSuites}
          initialFilters={initialFilters}
          ssrSeoData={ssrSeoData}
        />
        <Footer />
        <CardsModalRoot />
      </div>
    </ResponsiveProvider>
  );
};

export const getServerSideProps: GetServerSideProps<SuitesPageProps> = async context => {
  const { req } = context;
  const { urlFilter = '', fullUrlQuery = '', offset = 0 } = context.query;
  const reduxStore = initializeStore();
  const { dispatch, getState } = reduxStore;
  const activeFilterStyles =
    typeof urlFilter === 'string'
      ? getMotifsFromUrl(urlFilter).map(each => each.toUpperCase())
      : '';
  const activeFilterColors =
    typeof urlFilter === 'string'
      ? getSeoColorsFromUrl(urlFilter).map(each => each.toUpperCase())
      : '';
  const activeFilterShapes =
    typeof urlFilter === 'string'
      ? getSilhouettesFromUrl(urlFilter).map(each => each.toLowerCase())
      : '';
  const otherFilters = handleGetQueryParamsFromUrl(fullUrlQuery);

  const userContextPromise = fetchUser(req);

  const userAgent = req.headers['user-agent'];
  const isInitialMobile = new Device(userAgent).isMobile();
  const isInitialTablet = new Device(userAgent).isTablet();
  const isInitialDesktop = new Device(userAgent).isDesktop();

  const suitesRequestParams = {
    offset,
    limit: 48,
    motifs: activeFilterStyles,
    seo_colors: activeFilterColors,
    silhouettes: activeFilterShapes,
    highlight_design_family_uuid: null,
    check_drafts: true,
    ...otherFilters,
  };
  const WEDDING_PORT = process.env.ZOLA_ENV === 'production' ? '' : ':9003';
  const suitesPromise: Promise<any> = ApiService.post(
    `${process.env.WEB_HOST}${WEDDING_PORT}/web-api/v1/card-catalog/card-design-family/search/faceted`,
    suitesRequestParams,
    {},
    {
      ...ApiService.getCookieHeader(req as Request),
    }
  ).catch(e => {
    // @eslint-ignore-next-line no-console
    console.log('Full suites api error', e);
  });

  const tag = PLP_MODULE_GROUP_TAGS.MATCHING_SUITES;
  dispatch(requestModuleGroup());
  const moduleGroupPromise = ApiService.get(
    `${process.env.WEB_HOST}${WEDDING_PORT}/web-api/v1/module-group/tag/${tag}`,
    {},
    {
      ...ApiService.getCookieHeader(req as Request),
    }
  );

  const [userContextResponse, suitesResponse, moduleGroupResponse] = await Promise.allSettled([
    userContextPromise,
    suitesPromise,
    moduleGroupPromise,
    dispatch(getMotifs('DEFAULT')),
  ]);

  const userContext = userContextResponse.status === 'fulfilled' ? userContextResponse.value : null;
  if (userContext) {
    dispatch(receiveUser(userContext));
  }

  const initialSuites = (suitesResponse.status === 'fulfilled' ? suitesResponse.value : null) || {};

  const moduleGroup = moduleGroupResponse.status === 'fulfilled' ? moduleGroupResponse.value : null;
  if (moduleGroup) {
    dispatch(receiveModuleGroup(moduleGroup));
  }

  const initialReduxState = JSON.stringify(getState());

  const ssrSeoFilters = {
    motifs: activeFilterStyles || [],
    seo_colors: activeFilterColors || [],
    silhouettes: activeFilterShapes || [],
    ...otherFilters,
  };

  return {
    props: {
      initialReduxState,
      isInitialMobile,
      isInitialTablet,
      isInitialDesktop,
      initialSuites,
      initialFilters: suitesRequestParams,
      userContext,
      ssrSeoFilters,
    },
  };
};

export default Page;
